import React, { CSSProperties, ReactNode, useState } from 'react';

interface IconProps {
  d?: string;
  s?: number;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

export const Icon = ({ d, s = 22, children, style, className }: IconProps) => (
  <svg
    width={s}
    height={s}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="square"
    strokeLinejoin="miter"
    style={style}
    className={className}>
    {d ? <path d={d} /> : children}
  </svg>
);

interface EyebrowProps {
  children: ReactNode;
  color?: string;
  style?: CSSProperties;
}

export const Eyebrow = ({ children, color = 'var(--qb-orange-400)', style }: EyebrowProps) => (
  <div
    style={{
      fontFamily: 'var(--qb-font-display)',
      fontSize: 11,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color,
      display: 'inline-block',
      ...style,
    }}>
    {children}
  </div>
);

type BtnKind = 'primary' | 'secondary' | 'ghost' | 'parchment' | 'light';
type BtnSize = 'sm' | 'md' | 'lg';

interface BtnProps {
  kind?: BtnKind;
  size?: BtnSize;
  children: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  style?: CSSProperties;
}

const BTN_SIZES: Record<BtnSize, CSSProperties> = {
  sm: { padding: '8px 14px', fontSize: 11 },
  md: { padding: '12px 22px', fontSize: 12 },
  lg: { padding: '16px 28px', fontSize: 13 },
};

const BTN_KINDS: Record<BtnKind, CSSProperties> = {
  primary: {
    background: 'var(--qb-orange-500)',
    color: '#fff',
    border: '2px solid var(--qb-ink-900)',
    boxShadow: '5px 5px 0 var(--qb-ink-900)',
  },
  secondary: {
    background: 'transparent',
    color: 'var(--qb-fg)',
    border: '2px solid var(--qb-border-strong)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--qb-fg)',
    border: '2px solid transparent',
  },
  parchment: {
    background: 'var(--qb-ink-900)',
    color: 'var(--qb-white)',
    border: '2px solid var(--qb-ink-900)',
  },
  light: {
    background: 'var(--qb-cream-50)',
    color: 'var(--qb-ink-800)',
    border: '2px solid var(--qb-ink-900)',
    boxShadow: '5px 5px 0 var(--qb-ink-900)',
  },
};

export const Btn = ({
  kind = 'primary',
  size = 'md',
  children,
  leading,
  trailing,
  href = '#',
  target,
  rel,
  onClick,
  style,
}: BtnProps) => {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);

  const inter: CSSProperties = {};
  if (kind === 'primary' || kind === 'light') {
    if (press) {
      inter.transform = 'translate(2px,2px)';
      inter.boxShadow = '3px 3px 0 var(--qb-ink-900)';
    } else if (hover) {
      inter.transform = 'translate(-1px,-1px)';
      inter.boxShadow = '6px 6px 0 var(--qb-ink-900)';
    }
  } else if (kind === 'secondary' && hover) {
    inter.borderColor = 'var(--qb-orange-500)';
  } else if (kind === 'ghost' && hover) {
    inter.color = 'var(--qb-orange-400)';
  }

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      onClick={(e) => {
        if (onClick) {
          if (href === '#') e.preventDefault();
          onClick(e);
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setPress(false);
      }}
      onMouseDown={() => setPress(true)}
      onMouseUp={() => setPress(false)}
      style={{
        ...BTN_SIZES[size],
        ...BTN_KINDS[kind],
        fontFamily: 'var(--qb-font-display)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontWeight: 700,
        borderRadius: 4,
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        transition:
          'transform 120ms var(--qb-ease-standard), box-shadow 120ms var(--qb-ease-standard), color 120ms var(--qb-ease-standard), border-color 120ms var(--qb-ease-standard)',
        ...style,
        ...inter,
      }}>
      {leading}
      {children}
      {trailing}
    </a>
  );
};

interface D20Props {
  s?: number;
  color?: string;
  style?: CSSProperties;
}

export const D20 = ({ s = 64, color = 'currentColor', style }: D20Props) => (
  <svg
    width={s}
    height={s}
    viewBox="0 0 64 64"
    style={style}
    fill="none"
    stroke={color}
    strokeWidth="1.5"
    strokeLinejoin="miter">
    <polygon points="32,4 60,20 60,44 32,60 4,44 4,20" />
    <polygon points="32,4 32,32 4,20" />
    <polygon points="32,4 32,32 60,20" />
    <polygon points="32,32 32,60 4,44" />
    <polygon points="32,32 32,60 60,44" />
    <polygon points="32,32 4,20 4,44" />
    <polygon points="32,32 60,20 60,44" />
    <text
      x="32"
      y="38"
      textAnchor="middle"
      fontFamily="var(--qb-font-display)"
      fontSize="11"
      fill={color}
      stroke="none"
      letterSpacing="0.04em">
      20
    </text>
  </svg>
);

export const linkStyle: CSSProperties = {
  color: 'inherit',
  textDecoration: 'none',
  cursor: 'pointer',
};

export const listReset: CSSProperties = { listStyle: 'none', padding: 0, margin: 0 };
